import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';

type Role = 'admin' | 'agent';

interface Story {
  role: Role;
  title: string;
  desc: string;
  steps: string[];
  tip?: string;
}

interface SectionData {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  color: string;
  bgColor: string;
  stories: Story[];
}

const S_REGISTER: SectionData = {
  id: 'register',
  icon: 'ri-user-add-line',
  title: '新建代理商',
  subtitle: '由管理员创建代理商账号，不支持代理商自行申请',
  color: '#0069BD',
  bgColor: 'rgba(0,105,189,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：新建代理商账号',
      desc: '平台不支持代理商自行注册申请。所有代理商账号均由管理员在后台手动创建，账号创建后立即生效，无需额外审核环节。',
      steps: [
        '进入「代理商管理」，点击右上角「新建代理商」按钮',
        '填写基本信息：姓名、邮箱地址、公司名称（英文）、所在地区',
        '填写社交账号（可选）：支持绑定 Facebook、LinkedIn、Twitter 等平台及对应账号名，便于管理员统一查看代理商的社交媒体信息',
        '设置授权时间：选择账号有效期（90 天 / 180 天 / 1 年 / 2 年）',
        '配置资料权限：普通资料自动全部开放；如需授权机密资料，在「机密资料授权」区块勾选对应资料',
        '设置初始密码（可点击「随机生成」），填写备注（可选）',
        '点击「创建账号」完成，系统显示登录凭据，请通过安全渠道告知代理商',
      ],
      tip: '账号创建后立即生效，代理商可直接使用邮箱和初始密码登录。机密资料权限可在创建后随时在代理商详情中调整。',
    },
    {
      role: 'agent',
      title: '代理商：首次登录',
      desc: '代理商收到管理员提供的登录凭据后，直接访问平台登录页使用邮箱和密码登录，无需自行注册。',
      steps: [
        '访问平台登录页，输入管理员提供的邮箱和初始密码',
        '登录成功后进入资料库，可浏览和下载已授权的资料',
        '如需修改密码，可使用登录页「Forgot password?」功能自助重置',
      ],
      tip: '如遇登录问题，请联系管理员确认账号状态或重置密码。',
    },
  ],
};

const S_AGENTS: SectionData = {
  id: 'agents',
  icon: 'ri-team-line',
  title: '代理商管理',
  subtitle: '编辑、禁用、权限管理与活动追踪的完整流程',
  color: '#059669',
  bgColor: 'rgba(5,150,105,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：编辑代理商信息',
      desc: '点击代理商行末的编辑按钮，可修改姓名、公司、地区、有效期、账号状态和备注。',
      steps: [
        '点击操作列铅笔图标打开编辑弹窗',
        '可修改基本信息（姓名、公司名称、所在地区）',
        '账号有效期支持快速预设（+3 个月 / +6 个月 / +1 年 / +2 年），也可手动选择日期',
        '备注字段仅管理员可见，用于记录跟进事项或特殊说明',
        '点击「保存修改」生效',
      ],
    },
    {
      role: 'admin',
      title: '管理员：禁用 / 启用账号',
      desc: '启用中的账号可随时禁用，禁用后代理商立即无法登录；已禁用账号也可重新启用。',
      steps: [
        '对「启用中」账号，点击操作列禁止图标，弹窗确认后禁用',
        '对「已禁用」或「已过期」账号，点击绿色勾选图标立即重新启用',
        '支持勾选多个账号后批量禁用',
      ],
      tip: '禁用操作可随时撤销，账号数据不会丢失。',
    },
    {
      role: 'admin',
      title: '管理员：查看代理商详情页',
      desc: '点击代理商姓名或操作列的活动图标，可进入该代理商的独立详情页面，查看完整档案、活动概览、资料权限和文件访问明细。',
      steps: [
        '点击代理商姓名或操作列「活动记录」图标，跳转到代理商详情页',
        '左侧展示代理商档案（头像、公司、地区、有效期、社交账号、备注等）和活动概览',
        '「资料权限」标签页展示普通资料数、机密资料授权数、可见分类数，可直接勾选/取消勾选机密资料',
        '「文件访问明细」标签页列出该代理商访问过的每份资料的浏览次数、下载次数和最后访问日期，可点击资料名称跳转详情',
        '「分类权限设置」标签页可限定该代理商可见的产品型号文件夹范围',
      ],
    },
    {
      role: 'admin',
      title: '管理员：添加或编辑备注',
      desc: '在代理商列表中可直接查看备注图标，在详情页左侧也可编辑备注信息。',
      steps: [
        '在代理商列表中，有备注的账号会显示便签图标，鼠标悬停可查看完整备注内容',
        '无备注的账号显示「+」按钮，点击可直接添加备注',
        '在代理商详情页的「备注」区块中，可随时编辑或删除备注',
        '备注内容最多 500 字，仅管理员可见',
      ],
    },
    {
      role: 'admin',
      title: '管理员：重置密码',
      desc: '为代理商生成新的随机密码，需通过安全渠道线下告知。',
      steps: [
        '在代理商列表中点击操作列的锁形图标，打开重置密码弹窗',
        '系统自动生成 12 位随机密码，也可手动输入或再次点击「随机生成」刷新',
        '点击「确认重置」完成，密码立即生效',
      ],
      tip: '请通过安全的线下渠道（如邮件、即时通讯）将新密码告知代理商，切勿在平台内明文传输。',
    },
  ],
};

const S_MATERIALS: SectionData = {
  id: 'materials',
  icon: 'ri-folder-upload-line',
  title: '资料管理',
  subtitle: '上传、分级、分类、编辑的完整流程',
  color: '#7c3aed',
  bgColor: 'rgba(124,58,237,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：上传新资料',
      desc: '点击「上传资料」按钮，填写基本信息并上传文件，资料立即在代理商资料库中可见。上传时可根据文件类型配置对应的水印和下载策略。',
      steps: [
        '选择分类（可通过分类管理器新增/编辑分类）',
        '填写标题和描述',
        '选择是否设为「机密资料」（默认为普通）',
        '拖拽或点击上传文件（PDF / PPT），选择是否允许下载',
        '系统会根据文件类型自动显示水印配置项：PDF 显示「下载时叠加水印」开关；Word / Excel / PPT 显示不支持下载水印的说明；视频默认禁止下载',
        '点击「确认上传」完成',
      ],
      tip: 'E-learning 分类的资料自动识别为视频类型，下载功能自动禁用。若需下载水印保护，建议将 PPT 转成 PDF 后上传。',
    },
    {
      role: 'admin',
      title: '管理员：设置资料分级',
      desc: '资料分为「普通」和「机密」两级。普通资料所有注册成功的代理商均可查看；机密资料需手动为代理商授权。',
      steps: [
        '在资料列表中，点击操作列盾牌图标，可切换该资料的级别',
        '机密资料行显示红色「机密」标签及已授权人数',
        '点击已授权人数数字，弹窗勾选哪些代理商可查看该机密资料',
        '也可在「代理商管理」详情页的「资料权限」区块统一管理',
      ],
      tip: '可通过顶部筛选按钮快速查看全部普通 / 机密资料。',
    },
    {
      role: 'admin',
      title: '管理员：了解资料列表的分类列结构',
      desc: '资料列表采用三列分类展示结构，方便快速定位资料所属产品线和具体型号。',
      steps: [
        '「一级 / 二级分类」列：上方显示一级分类（如产品线、软件与系统），下方显示二级分类（如 CT Systems、Software）',
        '「三级分类（型号）」列：独立展示该资料对应的具体产品型号（如 InsitumCT 338、AlphaCT 868）；若资料适用全系列则显示「—」',
        '可通过顶部「全部分类」下拉筛选器按二级分类快速过滤资料',
        '点击「管理分类」按钮可新增/编辑一级、二级分类及三级产品型号',
      ],
      tip: '三级分类（型号）为选填项，适用于全系列的通用资料可不指定型号。',
    },
    {
      role: 'admin',
      title: '管理员：使用分类管理器',
      desc: '「管理分类」弹窗支持维护完整的三级分类体系：一级分类（产品线）、二级分类（子分类）和三级分类（产品型号）。',
      steps: [
        '点击「管理分类」按钮打开分类管理器',
        '一级分类不可增删，仅可查看其下的二级分类列表',
        '新增二级分类：选择所属一级分类，输入名称后点击「创建」',
        '编辑二级分类：点击铅笔图标修改名称',
        '删除二级分类：点击垃圾桶图标确认删除（会同时删除其下所有型号）',
        '展开二级分类可查看和管理其下的产品型号，支持新增、编辑、删除型号',
      ],
      tip: '删除分类或型号不会影响已上传的资料，但新上传时将无法选择被删除的分类。',
    },
    {
      role: 'admin',
      title: '管理员：编辑已有资料',
      desc: '在资料列表中点击编辑图标，或在资料详情页点击「编辑资源」按钮，可修改资料的所有信息。',
      steps: [
        '进入编辑页面，可修改标题、描述、分类（一级 / 二级 / 三级）、级别（普通 / 机密）',
        '可重新上传文件替换当前文件，或修改视频链接',
        '可调整下载权限（允许下载 / 仅浏览）',
        '可配置水印设置：预览水印始终开启；下载水印仅 PDF 支持',
        '点击「保存更改」生效',
      ],
    },
    {
      role: 'admin',
      title: '管理员：查看资料详情',
      desc: '点击资料标题或操作列的查看图标，进入资料详情页，可查看完整信息、预览文件、查看统计数据。',
      steps: [
        '详情页左侧展示文件预览（PDF/PPT 显示缩略图，视频可播放）',
        '右侧展示文件信息（格式、大小、分类、上传者、上传日期）、数据统计（总浏览量、下载量、下载率）',
        '权限设置区块显示当前的下载权限和机密授权人数',
        '可直接点击「编辑资源」进入编辑页面',
      ],
    },
    {
      role: 'admin',
      title: '管理员：配置产品型号文件夹权限',
      desc: '针对不同区域代理商销售不同产品型号的情况，可为每位代理商单独限定可见的产品型号文件夹，确保代理商仅能访问其负责销售的产品资料。',
      steps: [
        '点击代理商姓名打开详情页，切换到「分类权限设置」标签页',
        '或使用「资料权限」区块中的「资料分类权限」面板',
        '默认为「全部文件夹可见」；点击「自定义文件夹」切换为精细管控模式',
        '在文件夹列表中勾选该代理商有权访问的产品型号（如 CT Systems、Mobile CT 等）',
        '点击「保存分类权限」生效，代理商下次登录后仅能看到已授权文件夹中的资料',
      ],
      tip: '也可在「录入白名单」或「编辑代理商」弹窗的「产品型号文件夹权限」区块中直接配置，无需打开详情页。',
    },
    {
      role: 'admin',
      title: '管理员：在代理商处管理机密权限',
      desc: '打开某位代理商的详情页，切换到「资料权限」标签页，可直观看到该代理商当前的资料访问权限，并直接管理机密资料授权。',
      steps: [
        '点击代理商姓名打开详情页，切换到「资料权限」标签页',
        '顶部显示普通资料数（全部可见）/ 机密授权数 / 可见分类数',
        '在「机密资料授权」区块中勾选/取消勾选该代理商可查看的机密资料',
        '点击「保存权限」生效',
      ],
    },
  ],
};

const S_WATERMARK: SectionData = {
  id: 'watermark',
  icon: 'ri-shield-check-line',
  title: '水印设置与下载策略',
  subtitle: '动态水印保护文档，下载水印在资料编辑页逐份配置',
  color: '#b45309',
  bgColor: 'rgba(180,83,9,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：配置水印内容',
      desc: '水印自动叠加在所有在线预览文档上，包含三项内容，每项可单独开启或关闭。水印内容在「水印设置」页面统一配置，对所有资料生效。',
      steps: [
        '进入「水印设置」页面',
        '「用户账号」（动态）：每位代理商看到的水印含其自己的登录邮箱，一旦文件外传可立即溯源到具体账号',
        '「公司名称」（动态）：自动显示该代理商所属公司的英文名称，各代理商显示各自公司名',
        '「访问日期」（动态）：显示代理商预览文档当天的实际日期，实时更新',
        '点击每项右侧的开关即可开启或关闭该字段，点击「保存设置」使配置生效',
      ],
      tip: '水印样式（字体大小、透明度、角度）由系统统一设定，无需手动调整。以上配置仅控制在线预览水印内容，每份资料的下载水印需在资料编辑页面单独配置。',
    },
    {
      role: 'admin',
      title: '管理员：配置资料的下载水印',
      desc: '下载水印不再通过全局开关控制，而是在每份资料的编辑页面或上传页面中单独配置。不同文件格式对下载水印的支持不同，系统会根据文件类型智能显示对应的配置项。',
      steps: [
        '在「资料管理」中点击某份资料的「编辑」按钮，或在「上传资料」时填写水印配置',
        '所有格式的「预览水印」始终开启，不可关闭，系统会自动叠加在在线预览页面上',
        'PDF 文件：显示「下载时叠加水印」开关，开启后代理商下载的 PDF 将包含水印',
        'Word / Excel：显示说明「下载后为原始格式，系统无法嵌入水印」，不支持下载水印',
        'PPT 文件：显示说明「下载后为原始 PPT 格式，建议转 PDF 上传」，不支持下载水印',
        '视频资料：显示说明「禁止下载，无需配置」，视频默认禁止下载',
        '配置完成后点击「保存修改」或「确认上传」生效',
      ],
      tip: '只有 PDF 格式支持下载水印嵌入。如需 Word/PPT 下载后也有水印保护，建议先转成 PDF 再上传，并开启下载水印开关。',
    },
    {
      role: 'admin',
      title: '管理员：文件类型水印与下载策略说明',
      desc: '不同文件类型采用差异化的水印和下载策略。在线预览水印所有格式均强制叠加；下载水印仅 PDF 支持嵌入，其他格式下载后为原始文件。',
      steps: [
        'PDF 文件：在线浏览强制叠加水印；下载水印可在编辑页单独开启或关闭；下载权限也可单独配置',
        '视频资料（E-learning）：在线播放时强制叠加水印；默认禁止下载，无需配置下载水印',
        'PPT 文件：管理员自行决定是否允许下载；下载后为原始 PPT 格式，系统无法嵌入水印',
        'Word / Excel 等可编辑文档：强制仅支持在线浏览（有水印），禁止下载，无法手动开启下载权限',
      ],
      tip: '下载水印仅 PDF 格式支持嵌入。如需 PPT 下载后也有水印保护，建议先转成 PDF 再上传，并开启下载水印开关。水印设置页面提供各文件类型的策略总览，可随时查阅。',
    },
  ],
};

const S_ANALYTICS: SectionData = {
  id: 'analytics',
  icon: 'ri-bar-chart-box-line',
  title: '数据概览与数据分析',
  subtitle: '查看平台整体活动趋势、代理商活跃度和热门资料排行',
  color: '#374151',
  bgColor: 'rgba(55,65,81,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：查看数据概览',
      desc: '首页「数据概览」展示平台核心指标和趋势图表，帮助管理员快速了解整体运营情况。',
      steps: [
        '进入后台首页，顶部 KPI 卡片展示代理商总数、活跃数、期间浏览量和期间下载量',
        '点击时间筛选器（今日 / 7天 / 30天 / 1年 / 全部），所有数据会自动按时间范围重新计算',
        '活动趋势图表展示浏览量与下载量的变化曲线，支持鼠标悬停查看具体日期的数值',
        '底部左右并排展示代理商活动明细和热门资料排行',
      ],
    },
    {
      role: 'admin',
      title: '管理员：深入数据分析',
      desc: '「数据分析」页面提供更详细的排序功能和数据表格，支持多维度查看代理商活动和资料使用情况。',
      steps: [
        '进入「数据分析」页面，同样支持时间范围筛选（Today / 7 Days / 30 Days / All Time）',
        '顶部汇总卡片展示 Views、Downloads、Active Agents 和 Top Resource',
        '活动趋势图和热门代理商柱状图并排展示',
        '代理商活动明细表格支持按姓名、浏览量、下载量、最后活跃时间排序，点击表头即可切换排序',
        '热门资料排行表格支持按名称、浏览量、下载量、下载率排序',
      ],
    },
  ],
};

const S_EXPORT: SectionData = {
  id: 'export',
  icon: 'ri-download-2-line',
  title: '导出代理商数据',
  subtitle: '将代理商列表导出为 CSV 文件',
  color: '#374151',
  bgColor: 'rgba(55,65,81,0.06)',
  stories: [
    {
      role: 'admin',
      title: '管理员：导出代理商列表',
      desc: '在「代理商管理」页面，点击工具栏「导出」按钮，可将代理商数据导出为 CSV 文件。',
      steps: [
        '「导出当前筛选结果」：仅导出当前搜索 / 筛选后显示的代理商',
        '「导出全部代理商」：导出系统中所有代理商数据',
        '「导出已选中」：勾选特定代理商后，仅导出勾选项（需先勾选）',
        'CSV 文件包含姓名、邮箱、公司、地区、状态、社交账号、有效期等完整字段',
      ],
      tip: '导出文件采用 UTF-8 编码，可直接用 Excel 或 Google Sheets 打开。',
    },
  ],
};

const ALL_SECTIONS: SectionData[] = [
  S_REGISTER,
  S_AGENTS,
  S_MATERIALS,
  S_WATERMARK,
  S_ANALYTICS,
  S_EXPORT,
];

function RoleBadge({ role }: { role: Role }) {
  if (role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-800 text-white whitespace-nowrap">
        <i className="ri-shield-user-line text-xs"></i>管理员
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap"
      style={{ background: 'rgba(0,105,189,0.08)', borderColor: 'rgba(0,105,189,0.2)', color: '#0069BD' }}
    >
      <i className="ri-user-line text-xs"></i>代理商
    </span>
  );
}

function StoryCard({ story, index, color, bgColor }: { story: Story; index: number; color: string; bgColor: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold mt-0.5"
          style={{ background: color }}
        >
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <RoleBadge role={story.role} />
            <h3 className="text-sm font-bold text-gray-900">{story.title}</h3>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed">{story.desc}</p>
        </div>
      </div>
      <div className="px-5 py-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">操作步骤</p>
        <div className="space-y-2.5">
          {story.steps.map((step, si) => (
            <div key={si} className="flex items-start gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-xs font-bold"
                style={{ background: color, opacity: 0.85 }}
              >
                {si + 1}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed flex-1">{step}</p>
            </div>
          ))}
        </div>
        {story.tip && (
          <div className="mt-4 flex items-start gap-2.5 px-4 py-3 rounded-lg" style={{ background: bgColor }}>
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5" style={{ color }}>
              <i className="ri-lightbulb-line text-sm"></i>
            </div>
            <p className="text-xs leading-relaxed" style={{ color }}>
              <strong>提示：</strong>{story.tip}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionBlock({
  sec,
  activeSection,
  scrollToSection,
  sectionRef,
}: {
  sec: SectionData;
  activeSection: string;
  scrollToSection: (id: string) => void;
  sectionRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      id={sec.id}
      ref={sectionRef}
      className="scroll-mt-24"
    >
      <div className="flex items-center gap-4 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: sec.bgColor }}>
          <i className={`${sec.icon} text-lg`} style={{ color: sec.color }}></i>
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-900">{sec.title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">{sec.subtitle}</p>
        </div>
        <div className="flex-1 h-px bg-gray-100 ml-2"></div>
      </div>
      <div className="space-y-4">
        {sec.stories.map((story, idx) => (
          <StoryCard
            key={idx}
            story={story}
            index={idx}
            color={sec.color}
            bgColor={sec.bgColor}
          />
        ))}
      </div>
    </div>
  );
}

export default function ManualPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('register');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  return (
    <AdminLayout title="用户操作手册" subtitle="Sinovision 代理商平台完整使用指南">
      <div className="flex gap-8 items-start">

        {/* Sidebar TOC */}
        <aside className="hidden xl:block w-56 shrink-0 sticky top-24">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">目录</p>
            <nav className="space-y-0.5">
              {ALL_SECTIONS.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollToSection(sec.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer text-left whitespace-nowrap ${
                    activeSection === sec.id
                      ? 'font-semibold text-white'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                  style={activeSection === sec.id ? { background: sec.color } : {}}
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className={`${sec.icon} text-xs`}></i>
                  </div>
                  <span className="truncate text-xs">{sec.title}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="mt-4 bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">角色说明</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <RoleBadge role="admin" />
                <span className="text-xs text-gray-500">后台管理人员</span>
              </div>
              <div className="flex items-center gap-2">
                <RoleBadge role="agent" />
                <span className="text-xs text-gray-500">代理商用户</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-10">

          {/* Hero */}
          <div className="rounded-2xl relative" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' }}>
            <div className="px-8 py-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <i className="ri-book-open-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">User Manual</p>
                  <h1 className="text-white text-xl font-bold">用户操作手册</h1>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                本手册涵盖 Sinovision 代理商资料分发平台的完整使用流程，包括新建代理商、代理商管理与活动追踪、资料管理与分类、机密权限、水印设置与下载策略、数据概览与深入分析、数据导出等功能的操作步骤与使用技巧。
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                {ALL_SECTIONS.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 text-white/80 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/10"
                  >
                    <i className={`${sec.icon} text-xs`}></i>
                    {sec.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Each section rendered explicitly */}
          <SectionBlock sec={S_REGISTER} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('register')} />
          <SectionBlock sec={S_AGENTS} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('agents')} />
          <SectionBlock sec={S_MATERIALS} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('materials')} />
          <SectionBlock sec={S_WATERMARK} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('watermark')} />
          <SectionBlock sec={S_ANALYTICS} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('analytics')} />
          <SectionBlock sec={S_EXPORT} activeSection={activeSection} scrollToSection={scrollToSection} sectionRef={setRef('export')} />

          {/* Footer */}
          <div className="rounded-xl border border-gray-100 bg-white px-6 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm font-bold text-gray-800">需要更多帮助？</p>
              <p className="text-xs text-gray-400 mt-0.5">如有疑问，请联系 Sinovision 技术支持团队</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: '#0069BD' }}
            >
              <i className="ri-arrow-left-line text-sm"></i>
              返回后台
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
